/*
 * Pure fish-catch helpers. No Preact, no stores, no fetch — everything here is
 * a plain function so it can be unit tested directly.
 *
 * NB: `catch` is a JS reserved word. Every identifier uses the fishCatch prefix.
 * Module-level constants below are shared for the whole session — never mutate
 * them; always return new arrays.
 */

export { exifGpsToDecimal } from './exifGps';

// Seeded from the Norwegian vocabulary in backend/lib/ai/fishing-content-analyzer.js.
// Duplicated rather than imported — the webapp does not reach into backend code.
// [value, label] pairs — the datalist format used throughout fields.js
// (input.js only reads datalist as el[0]/el[1] pairs, see components/form/input.js).
export const FISH_SPECIES = [
    ['laks', 'Laks'],
    ['sjøørret', 'Sjøørret'],
    ['ørret', 'Ørret'],
    ['røye', 'Røye'],
    ['harr', 'Harr'],
    ['gjedde', 'Gjedde'],
    ['abbor', 'Abbor'],
    ['sik', 'Sik'],
    ['regnbueørret', 'Regnbueørret'],
    ['torsk', 'Torsk'],
    ['sei', 'Sei'],
    ['makrell', 'Makrell'],
];

// [value, label] pairs — the datalist format used throughout fields.js.
export const CATCH_METHODS = [
    ['dry-fly', 'Tørrflue'],
    ['nymph', 'Nymfe'],
    ['wet-fly', 'Våtflue'],
    ['streamer', 'Streamer'],
    ['spinning', 'Sluk/spinn'],
    ['bait', 'Agn/mark'],
];

export const WATER_TYPES = [
    ['river', 'Elv'],
    ['lake', 'Innsjø'],
    ['sea', 'Sjø'],
];

// Used for public display only — the exact value stays in the database so a
// user can reveal the precise spot later without having lost precision.
// 2 decimals ≈ 1.1 km.
export function roundCoordinate(value, decimals = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return null;
    }
    const factor = 10 ** decimals;
    return Math.round(number * factor) / factor;
}

// All three yr.no code paths (fresh, cached, historical) return entries shaped
// { time, instant: { details }, next_1_hours: {...} }, so one normaliser covers
// them all. Historical entries have no precipitation details.
export function normalizeYrEntry(entry) {
    if (!entry) {
        return null;
    }
    const details = entry.instant?.details || {};
    const symbolCode = entry.next_1_hours?.summary?.symbol_code
        || entry.next_6_hours?.summary?.symbol_code
        || '';
    const precipitation = entry.next_1_hours?.details?.precipitation_amount
        ?? entry.next_6_hours?.details?.precipitation_amount
        ?? null;
    const orNull = value => (value === undefined ? null : value);
    return {
        temperature: orNull(details.air_temperature),
        windSpeed: orNull(details.wind_speed),
        windDirection: orNull(details.wind_from_direction),
        humidity: orNull(details.relative_humidity),
        pressure: orNull(details.air_pressure_at_sea_level),
        symbolCode,
        precipitation,
        observedTime: orNull(entry.time),
    };
}

// Distinct previously-used values for a free-text field, most used first.
export function collectSuggestions(fishCatches, key, limit = 50) {
    const counts = new Map();
    (Array.isArray(fishCatches) ? fishCatches : []).forEach(fishCatch => {
        const raw = fishCatch && fishCatch[key];
        if (typeof raw !== 'string') {
            return;
        }
        const value = raw.trim();
        if (!value) {
            return;
        }
        const lookupKey = value.toLowerCase();
        const existing = counts.get(lookupKey);
        if (existing) {
            existing.count += 1;
        } else {
            counts.set(lookupKey, { value, count: 1 });
        }
    });
    return [...counts.values()]
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
        .slice(0, limit)
        .map(entry => entry.value);
}

export function formatCatchHeadline(fishCatch) {
    if (!fishCatch) {
        return '';
    }
    const parts = [];
    const species = String(fishCatch.species || '').trim();
    if (species) {
        parts.push(species.charAt(0).toUpperCase() + species.slice(1));
    }
    if (fishCatch.lengthCm > 0) {
        parts.push(`${fishCatch.lengthCm} cm`);
    }
    if (fishCatch.weightKg > 0) {
        parts.push(`${fishCatch.weightKg} kg`);
    }
    return parts.join(' · ');
}
